import './crop-image-panel.less';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Scrollbars, SliderInput } from '@meitu/saas-ui';
import { BackTitle } from '@meitu/saas-ui/es/title';
import { Editor } from '@meitu/saas-editor-core/es/index';
import { type CropImgPlugin } from '@meitu/saas-editor-core/es/layers/image-layer/plugins/apps/user-editor/crop-img';
import { isLayerSizeRadio } from '@meitu/saas-editor-core/es/utils/image-layer-crop';
import { IImageLayerProps, ILayerProps } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import LabelAction from '@/components/label-action';
import { getLocaleStore, useLocaleStore } from '@/i18n/index';

const ExpandRadioOptions = () => {
    const { $t } = getLocaleStore();

    return [
        {
            label: $t((v) => v.collapse.adjustCollapse.freeCutting),
            value: '0',
            icon: 'CropBold',
        },
        {
            label: '1:1',
            value: '1:1',
            icon: 'Ratio11Bold',
        },
        {
            label: '3:2',
            value: '3:2',
            icon: 'Ratio32Bold',
        },
        {
            label: '2:3',
            value: '2:3',
            icon: 'Ratio23Bold',
        },
        {
            label: '4:3',
            value: '4:3',
            icon: 'Ratio43Bold',
        },
        {
            label: '3:4',
            value: '3:4',
            icon: 'Ratio34Bold',
        },
        {
            label: '16:9',
            value: '16:9',
            icon: 'Ratio169Bold',
        },
        {
            label: '9:16',
            value: '9:16',
            icon: 'Ratio916Bold',
        },
    ];
};

const formatRotate = (rotate: number) => {
    return rotate > 180 ? rotate - 360 : rotate;
};

const CropImgPanel: React.FC<{
    setPanel: Function;
    editor: Editor;
    setIsDisableAppAndShowPanel: (isDisableAppAndShowPanel: boolean) => void;
}> = ({ setPanel, editor, setIsDisableAppAndShowPanel }) => {
    const { $t } = useLocaleStore();
    const cropImgPlugin = useMemo(() => editor.getInstance<CropImgPlugin>('CropImgPlugin'), [editor]);
    const initialLayer = cropImgPlugin.getCurrentLayer();
    const [activeLayer, setActiveLayer] = useState<ILayerProps | null>(initialLayer);
    const [ratio, setRatio] = useState(() =>
        initialLayer ? isLayerSizeRadio(initialLayer.width, initialLayer.height) || '0' : '0',
    );
    const [isApplying, setIsApplying] = useState(false);

    const cancelCropImage = useCallback(() => {
        if (cropImgPlugin.cancel()) {
            setPanel(null);
        }
    }, [cropImgPlugin, setPanel]);

    const confirmCropImage = useCallback(async () => {
        const applied = await cropImgPlugin.apply();
        if (applied) {
            setPanel(null);
        }
    }, [cropImgPlugin, setPanel]);

    useEffect(() => {
        const offCurrentLayer = cropImgPlugin.watchCurrentLayer((layer, applying) => {
            setActiveLayer(layer);
            setIsApplying(applying);
        });
        const escHandle = editor.onEsc(() => {
            cancelCropImage();
        });

        return () => {
            editor.off(escHandle);
            offCurrentLayer();
        };
    }, [cancelCropImage, cropImgPlugin, editor]);

    useEffect(() => {
        setIsDisableAppAndShowPanel(true);

        return () => {
            setIsDisableAppAndShowPanel(false);
        };
    }, [setIsDisableAppAndShowPanel]);

    useEffect(() => {
        const handler = editor.onMouseDown((e) => {
            if (!cropImgPlugin.checkIsOperateTarget(e.target)) {
                void confirmCropImage();
            }
        }, 1000);
        return () => {
            editor.off(handler);
            cropImgPlugin.close();
        };
    }, [confirmCropImage, cropImgPlugin, editor]);

    return (
        <div className="c-crop-image-panel">
            <Scrollbars style={{ width: '100%', height: '100%' }}>
                <div className="title">
                    <BackTitle
                        text={$t((v) => v.panel.imagePanel.imageCrop)}
                        handleBackClick={() => {
                            void confirmCropImage();
                        }}
                    />
                </div>

                <div className="ratio-list">
                    {ExpandRadioOptions().map((option) => {
                        return (
                            <div
                                key={option.value}
                                onClick={() => {
                                    setRatio(option.value);
                                    cropImgPlugin.setRatio(option.value);
                                }}
                                className={classNames({
                                    item: true,
                                    active: option.value === ratio,
                                })}
                            >
                                <IconFont name={option.icon} fontSize={20} />
                                {option.label}
                            </div>
                        );
                    })}
                </div>

                <div className="rotate-wrap">
                    <LabelAction title={$t((v) => v.panel.markupPanel.rotate)}>
                        <div className="input-wrap">
                            <SliderInput
                                value={formatRotate(
                                    parseInt(`${(activeLayer?.data as IImageLayerProps)?.imageTransform.rotate || 0}`),
                                )}
                                showStepBtn={true}
                                max={180}
                                min={-180}
                                type="horizontal"
                                onChange={(val: number) => {
                                    cropImgPlugin.setRotate(val);
                                }}
                            />
                        </div>
                    </LabelAction>
                </div>
                <div className="operate">
                    <Button
                        type="ghost"
                        disabled={isApplying}
                        onClick={() => {
                            cancelCropImage();
                        }}
                    >
                        {$t((v) => v.imageEffectPanel.imageBlur.cancel)}
                    </Button>
                    <Button
                        className="done"
                        loading={isApplying}
                        onClick={() => {
                            void confirmCropImage();
                        }}
                    >
                        {$t((v) => v.imageEffectPanel.imageBlur.apply)}
                    </Button>
                </div>
            </Scrollbars>
        </div>
    );
};

export default CropImgPanel;
